// Empire Apps Chassis · connectors/financial/razorpay-history
// Razorpay merchant account — read-only payment + subscription history.
// For empire users running businesses: founders, CAs, salons, clinics.
//
// Razorpay key (key_id) is publishable; key_secret stays server-side.
// Live key (publishable side) is wired by the apps as build-time env.

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type RazorpayPaymentRecord,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';

const DEV = true;
const STATUS_KEY = 'empire:financial:razorpay-history:status';
const RZP_BASE = 'https://api.razorpay.com/v1';

interface PersistedStatus {
  readonly connected: boolean;
  readonly keyId: string | null;
  readonly lastSync: string | null;
  readonly importedCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, keyId: null, lastSync: null, importedCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

interface RzpPayment {
  readonly id: string;
  readonly order_id: string | null;
  readonly amount: number;
  readonly status: 'authorized' | 'captured' | 'refunded' | 'failed';
  readonly method: string;
  readonly email: string | null;
  readonly contact: string | null;
  readonly created_at: number;
}

interface RzpPaymentsResponse {
  readonly count: number;
  readonly items: readonly RzpPayment[];
}

export async function fetchRazorpayPayments(keyId: string, keySecret: string, from: number | null = null, count = 50): Promise<readonly RazorpayPaymentRecord[]> {
  const url = new URL(`${RZP_BASE}/payments`);
  url.searchParams.set('count', String(count));
  if (from !== null) url.searchParams.set('from', String(from));
  const auth = btoa(`${keyId}:${keySecret}`);
  const r = await fetch(url.toString(), { headers: { Authorization: `Basic ${auth}` } });
  if (!r.ok) throw new Error(`Razorpay ${r.status}`);
  const j = (await r.json()) as RzpPaymentsResponse;
  return j.items.map((p) => ({
    paymentId: p.id,
    orderId: p.order_id,
    amountPaise: paise(p.amount),
    status: p.status,
    method: p.method,
    email: p.email,
    contact: p.contact,
    createdAt: new Date(p.created_at * 1000).toISOString() as ReturnType<typeof isoTs>,
  }));
}

function mockPayments(): readonly RazorpayPaymentRecord[] {
  const now = Date.now();
  return [
    { paymentId: 'pay_O8ZqK1Ax', orderId: 'order_pro_2026_001', amountPaise: paise(99900), status: 'captured', method: 'upi', email: 'aditya@taxwalaai.com', contact: '+919999999999', createdAt: new Date(now - 86_400_000).toISOString() as ReturnType<typeof isoTs> },
    { paymentId: 'pay_O8ZqK2Bx', orderId: 'order_family_2026_001', amountPaise: paise(299900), status: 'captured', method: 'netbanking', email: 'family@taxwalaai.com', contact: '+919888888888', createdAt: new Date(now - 2 * 86_400_000).toISOString() as ReturnType<typeof isoTs> },
    { paymentId: 'pay_O8ZqK3Bx', orderId: 'order_bundle_2026_001', amountPaise: paise(299900), status: 'captured', method: 'card', email: 'sheth@taxwalaai.com', contact: '+919777777777', createdAt: new Date(now - 5 * 86_400_000).toISOString() as ReturnType<typeof isoTs> },
  ];
}

export const razorpayHistory: Connector = {
  id: 'razorpay-history',
  displayName: 'Razorpay merchant history',
  category: 'payment-gateway',
  defaultMode: DEV ? 'mock' : 'live',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: DEV ? 'mock' : 'live',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.importedCount,
      disabledReason: DEV ? null : 'key_secret must be stored server-side; browser-only flow is read-only mock.',
    };
  },

  async connect(ctx: ConnectorContext, opts?: Record<string, string>): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    const keyId = opts?.keyId ?? 'rzp_live_SVzHpgz5EgB4bk';
    logConnectorConsent(ctx.dpdpLog, 'connector-razorpay-read', `Razorpay merchant ${keyId}`);
    const connectedAt = isoTs();
    writeStatus({ connected: true, keyId, lastSync: connectedAt, importedCount: 0 });
    return ok({ connectedAt });
  },

  async import(): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    const payments = mockPayments();
    writeStatus({ ...s, lastSync: isoTs(), importedCount: payments.length });
    return ok({ razorpay: payments });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, keyId: null, lastSync: null, importedCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-razorpay-read' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { razorpay: mockPayments() };
  },
};
